
import React from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  locale?: string;
  siteName?: string;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description = "Buche deinen Wunschtermin im Lieblingsstudio – einfach, schnell & smart mit CHRONA.",
  canonicalPath,
  ogImage = "https://chrona.it/og-default.jpg",
  ogType = "website",
  noindex = false,
  keywords = "Terminbuchung, Online-Termine, Friseur, Nagelstudio, Kosmetik, Wellness, CHRONA",
  author = "CHRONA",
  publishedTime,
  modifiedTime,
  locale = "de_DE",
  siteName = "CHRONA – Dein Studio. Dein Termin."
}) => {
  const siteTitle = siteName;
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const canonical = canonicalPath ? `https://chrona.it${canonicalPath}` : "https://chrona.it";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title || "CHRONA – Studio buchen leicht gemacht"} />
      <meta property="og:description" content={description || "Finde Studios in deiner Nähe & buche direkt online"} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="CHRONA" />
      
      {/* Article specific tags */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || "CHRONA – Studio buchen leicht gemacht"} />
      <meta name="twitter:description" content={description || "Finde Studios in deiner Nähe & buche direkt online"} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Indexing directives */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Language */}
      <meta property="og:locale" content={locale} />
      <meta httpEquiv="content-language" content={locale.replace('_', '-')} />
    </Helmet>
  );
};

// Export SEO configuration for dynamic pages
export const seoConfig = {
  // Dynamic meta generation for studio pages
  dynamicMeta: async ({ studioSlug }: { studioSlug: string }) => {
    try {
      const { data: studio, error } = await fetch(`/api/studios/${studioSlug}`).then(res => res.json());
      
      if (error || !studio) return null;

      return {
        title: `${studio.name} buchen – CHRONA`,
        description: studio.description || 'Professionelle Services über CHRONA buchen',
        ogImage: studio.image_url || 'https://chrona.it/og-default.jpg',
        url: `https://chrona.it/studio/${studio.slug}`,
        ogType: 'business.business'
      };
    } catch (error) {
      console.error('Error fetching dynamic meta:', error);
      return null;
    }
  },
  
  // Default robots.txt content
  robotsTxt: `
User-agent: *
Allow: /
Disallow: /admin
Disallow: /admin-setup
Disallow: /testing
Disallow: /api/
Disallow: /auth/
Disallow: /dashboard
Disallow: /studio-dashboard/
Disallow: /account/

# Search engine specific rules
User-agent: Googlebot
Allow: /
Allow: /studio/
Allow: /sitemap.xml

User-agent: Bingbot
Allow: /
Allow: /studio/
Allow: /sitemap.xml

# Social media crawlers
User-agent: Twitterbot
Allow: /
Allow: /studio/

User-agent: facebookexternalhit
Allow: /
Allow: /studio/

# Sitemap location
Sitemap: https://chrona.it/sitemap.xml
  `
};
